import React from "react";
import * as PropTypes from "prop-types";
import clsx from "clsx";
import { useTranslate } from "react-polyglot";
import { useHistory } from "react-router-dom";
import { makeStyles, Typography, Box } from "@material-ui/core";
import useCompanyTitle from "../../../../hooks/useCompanyTitle.js";
import { useCourseProvider } from "../../../../Providers/Data/CourseProvider.jsx";
import FlexboxHorizontal from "../../../../SharedComponents/FlexboxHorizontal.jsx";
import FlexboxVertical from "../../../../SharedComponents/FlexboxVertical.jsx";
import LinearProgressBar from "../../../../SharedComponents/LinearProgressBar.jsx";
import { randomColor } from "../../../../Utils/misc.js";
import { ReactComponent as IconLock } from "../../../../assets/Icons/ic_lock_2.svg";

const useStyles = makeStyles((theme) => ({
  branchCard: {
    width: 235,
    height: 320,
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    marginLeft: 18,
    marginBottom: 18,
    borderTop: "3px solid",
    borderRadius: 12,
    color: "#1B1C1C",
    backgroundColor: "#fff",
    cursor: "pointer",
    boxShadow: "0 0 16.53px rgba(0, 0, 0, 0.12)",
    "&:hover": {
      background: `linear-gradient(rgba(0,0,0,0.03), rgba(0,0,0,0.03)), #fff`,
    },
    [theme.breakpoints.down("md")]: {
      marginLeft: 40,
    },
  },
  branchCardDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  branchImageBox: {
    width: 200,
    height: 200,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
  },
  image: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  branchName: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.4,
    height: 45,
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  branchPercentBox: {
    marginLeft: 8,
  },
  branchPercent: {
    fontSize: 16,
    "&.MuiTypography-colorSecondary": {
      color: "#58A065",
    },
  },
  branchProgress: {
    "& .MuiLinearProgress-bar": {
      borderRadius: 5,
    },
    "& .MuiLinearProgress-barColorSecondary": {
      backgroundColor: "#58A065",
    },
  },
  branchProgressDisabled: {
    "& .MuiLinearProgress-bar": {
      borderRadius: 5,
    },
    "&.MuiLinearProgress-colorPrimary": {
      backgroundColor: "#54606B",
      opacity: 0.1,
    },
  },
}));

/**
 * @param {Branch} branch
 * @returns {*}
 * @constructor
 */
const BranchCardVertical = ({ branch }) => {
  const classes = useStyles();
  const t = useTranslate();
  const { saveCurrentBranch } = useCourseProvider();
  const history = useHistory();

  useCompanyTitle("title.home");

  /** */
  const onClickBranch = () => {
    if (!branch.open) return;
    saveCurrentBranch(branch);
    history.push("/decks");
  };

  return (
    <FlexboxVertical
      onClick={() => onClickBranch(branch)}
      className={clsx(classes.branchCard, {
        [classes.branchCardDisabled]: !branch.open,
      })}
      style={{
        borderColor: branch.color || randomColor(),
      }}
    >
      <Typography className={classes.branchName}>{branch.name}</Typography>
      {(branch.illustrationImage || branch.illustrationImageUrl) && (
        <Box className={classes.branchImageBox}>
          <img
            className={classes.image}
            src={branch.illustrationImage?.url || branch.illustrationImageUrl}
            alt="image of topic"
          />
        </Box>
      )}
      <FlexboxHorizontal alignItems="center">
        <LinearProgressBar
          className={branch.open ? classes.branchProgress : classes.branchProgressDisabled}
          variant="determinate"
          value={branch.percent}
          height={10}
          color={branch.percent >= 99 ? "secondary" : "primary"}
          style={{
            borderRadius: 5,
            width: 160,
          }}
          noborder
          resetBackgroundColor
        />
        {!branch.open && (
          <Box className={classes.branchPercentBox}>
            <IconLock />
          </Box>
        )}
      </FlexboxHorizontal>
    </FlexboxVertical>
  );
};

BranchCardVertical.propTypes = {
  branch: PropTypes.object,
};

export default BranchCardVertical;
